import { format, parseISO } from 'date-fns';
import { Link } from 'react-router-dom';
import Card from '../ui/Card';
import PlaceLink from '../layout/PlaceLink';
import SaveToMyOrbconButton from '../saved/SaveToMyOrbconButton';
import type { CatalogResource } from '../../types/resourceCatalog';
import { catalogLabel } from '../../utils/catalogLabels';
import { decodeHtmlEntities } from '../../utils/htmlEntities';
import type { RowPlace } from '../../utils/libraryPlaces';
import { buildResourcePath } from '../../utils/resourceLinks';
import RevisionNotice from './RevisionNotice';

function formatWhen(value: string | null): string | null {
  if (!value) return null;
  const date = parseISO(value);
  if (Number.isNaN(date.getTime())) return null;
  return format(date, 'MMMM d, yyyy');
}

function isDocumentAttachment(attachment: CatalogResource['attachments'][number]): boolean {
  const mime = (attachment.mime_type || '').toLowerCase();
  if (mime.startsWith('image/')) return false;
  const ext = attachment.url.split('?')[0].split('#')[0].split('.').pop()?.toLowerCase() || '';
  if (mime === 'application/pdf' || ext === 'pdf') return true;
  if (mime.startsWith('application/') || mime.startsWith('text/') || mime.startsWith('video/')) return true;
  return ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'csv', 'mp4', 'mov', 'webm'].includes(ext);
}

function attachmentLabel(title: string | null, mime: string | null): string {
  const trimmed = title?.trim();
  if (trimmed) return trimmed;
  if (mime?.includes('pdf')) return 'PDF';
  if (mime?.includes('word') || mime?.includes('officedocument')) return 'Word';
  return 'File';
}

export default function CatalogResourceRow({
  resource,
  places = [],
}: {
  resource: CatalogResource;
  places?: RowPlace[];
}) {
  const published = formatWhen(resource.wpCreatedAt);
  const updated = formatWhen(resource.wpModifiedAt);
  const showUpdated = updated && updated !== published;
  const title = decodeHtmlEntities(resource.title);
  const topics = resource.topics.map(catalogLabel);
  const documents = resource.attachments.filter(isDocumentAttachment);

  return (
    <Card className="!p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {resource.contentType && (
            <p className="text-xs font-medium text-gray-500 mb-1">{catalogLabel(resource.contentType)}</p>
          )}
          <h3 className="text-base font-bold text-gray-900">
            <Link to={buildResourcePath(resource.slug)} className="hover:text-primary">
              {title}
            </Link>
          </h3>
        </div>
        <SaveToMyOrbconButton
          type="RESOURCE"
          title={title}
          slug={resource.slug}
          postId={resource.wpId}
          variant="icon"
          align="end"
        />
      </div>
      {topics.length > 0 && (
        <p className="mt-2 text-sm text-gray-600">{topics.join(', ')}</p>
      )}
      {places.length > 0 && (
        <p className="mt-2 text-sm text-gray-600">
          {places[0].kind === 'hub' ? 'In ' : 'Part of '}
          {places.map((place, index) => (
            <span key={place.href}>
              {index > 0 && ', '}
              <PlaceLink
                to={place.href}
                surface={{ kind: place.kind, slug: place.slug, via: 'link' }}
                className="font-medium text-primary hover:text-primary-dark focus-ring rounded-sm"
              >
                {place.title}
              </PlaceLink>
            </span>
          ))}
        </p>
      )}
      {resource.underRevision && <RevisionNotice className="mt-2" />}
      {(published || showUpdated) && (
        <p className="mt-2 text-sm text-gray-500">
          {published && <span>Published {published}</span>}
          {published && showUpdated && <span> · </span>}
          {showUpdated && <span>Updated {updated}</span>}
        </p>
      )}
      {documents.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
          {documents.map((attachment) => (
            <li key={attachment.id}>
              <a
                href={attachment.url}
                className="text-sm text-primary hover:text-primary-dark font-medium"
                target="_blank"
                rel="noopener noreferrer"
              >
                {decodeHtmlEntities(attachmentLabel(attachment.title, attachment.mime_type))}
              </a>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
